import React, { useState, useEffect } from "react";
import {
  Clock,
  Video,
  Save,
  Loader,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Lock,
  Mail,
  Bell,
} from "lucide-react";
import {
  getInterviewSettings,
  updateInterviewSettings,
  getSystemServiceSettings,
  updateSystemServiceSettings,
  getCurrentLanguage,
  getAvailableInterviewLanguages,
  setGlobalLanguage,
  getListingSettings,
  updateListingSettings,
  getRoles,
  getRBACSettings,
  updateRBACSettings,
  getEmailConfig,
  updateEmailConfig,
  updateVideoProctoringConfig,
  getVideoProctoringConfig,
  getReminderConfig,
  updateReminderConfig,
  getJobDivaSettings,
  updateJobDivaSettings,
} from "../../services/settingsapi";
import ConfirmationDialog from "../ConfirmationDialog";
import {
  SettingsSection,
  InputField,
  SelectField,
  ToggleSwitch,
} from "../SettingsUIComponents";
import { useRBAC } from "../../context/RBACContext";
import {
  RESOURCE_DISPLAY_NAMES,
  ACTION_NAMES,
} from "../../utils/resourceMapping";

const interviewLanguageFlags = {"English": "GB", "Spanish": "ES", "French": "FR", "German": "DE", "Hindi": "IN", "Portuguese": "PT", "Italian": "IT", "Japanese": "JP", "Korean": "KR", "Chinese": "CN", "Arabic": "SA", "Dutch": "NL", "Russian": "RU"};
const languageFlag = language => {
  const country = interviewLanguageFlags[language];
  return country ? String.fromCodePoint(...country.split(" ").join("").split("").map(letter => 127397 + letter.charCodeAt(0))) : String.fromCodePoint(127760);
};

const InterviewSettings = ({ canEditSettings, canViewSettings }) => {
  // RBAC Integration
  const { hasResourcePermissionByName, permissions } = useRBAC();

  // Determine permissions based on RBAC context
  const rbacCanViewSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.GET_DATA,
  );

  const rbacCanEditSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.EDIT_ITEM,
  );

  // Use RBAC permissions if available, otherwise fall back to props
  const effectiveCanViewSettings =
    rbacCanViewSettings !== undefined ? rbacCanViewSettings : canViewSettings;
  const effectiveCanEditSettings =
    rbacCanEditSettings !== undefined ? rbacCanEditSettings : canEditSettings;

  // Interview Settings
  const [interviewSettings, setInterviewSettings] = useState({
    MARKS_PER_QUESTION: 10,
    PASSING_SCORE_PERCENTAGE: 75,
    INTERVIEW_LINK_EXPIRY: 40,
  });
  const [interviewErrors, setInterviewErrors] = useState({});
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewMessage, setInterviewMessage] = useState({
    type: "",
    text: "",
  });

  // Language Settings
  const [languageSettings, setLanguageSettings] = useState({
    currentLanguage: "English",
    supportedLanguages: [],
    capabilities: [],
  });
  const [languageLoading, setLanguageLoading] = useState(false);
  const [languageMessage, setLanguageMessage] = useState({
    type: "",
    text: "",
  });

  // System Service Settings
  const [systemServiceSettings, setSystemServiceSettings] = useState({
    SYSTEM_CHECK_ENABLE: false,
    SECOND_CAMERA_ENABLE: false,
    CODE_EVALUATION_ENABLE: false,
    RESUME_EVALUATION_ENABLE: false,
  });
  const [systemServiceLoading, setSystemServiceLoading] = useState(false);
  const [systemServiceMessage, setSystemServiceMessage] = useState({
    type: "",
    text: "",
  });

  // Listing Settings
  const [listingSettings, setListingSettings] = useState({
    JOB_OPENING_DEFAULT_DAYS: 30,
    SCHEDULE_DEFAULT_DAYS: 15,
  });
  const [listingErrors, setListingErrors] = useState({});
  const [listingLoading, setListingLoading] = useState(false);
  const [listingMessage, setListingMessage] = useState({ type: "", text: "" });

  // RBAC Settings
  const [rbacSettings, setRbacSettings] = useState({
    DEFAULT_USER_ROLE_ID: null,
  });
  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rbacErrors, setRbacErrors] = useState({});
  const [rbacLoading, setRbacLoading] = useState(false);
  const [rbacMessage, setRbacMessage] = useState({ type: "", text: "" });

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [emailConfig, setEmailConfig] = useState({
    schedule_email_enabled: false,
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState({ type: "", text: "" });

  const [videoProctoringEnabled, setVideoProctoringEnabled] = useState(false);
  const [videoProctoringLoading, setVideoProctoringLoading] = useState(false);
  
  // Reminder Configuration
  const [reminderConfig, setReminderConfig] = useState({
    enabled: true,
    reminders: [
      { offset_minutes: 1440, offset_label: "1 day before" },
      { offset_minutes: 360, offset_label: "6 hours before" },
      { offset_minutes: 60, offset_label: "1 hour before" }
    ]
  });
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderMessage, setReminderMessage] = useState({ type: "", text: "" });

  const [jobDivaSettings, setJobDivaSettings] = useState({
    clientId: "",
    username: "",
    password: "",
    passwordDisplay: "********",
  });
  const [jobDivaErrors, setJobDivaErrors] = useState({});
  const [jobDivaLoading, setJobDivaLoading] = useState(false);
  const [jobDivaMessage, setJobDivaMessage] = useState({ type: "", text: "" });

  const fetchEmailConfig = async () => {
    try {
      setEmailLoading(true);
      const data = await getEmailConfig();
      setEmailConfig(data);
    } catch (error) {
      setEmailMessage({
        type: "error",
        text: "Error loading email config: " + error.message,
      });
    } finally {
      setEmailLoading(false);
    }
  };
  const fetchVideoProctoring = async () => {
    try {
      setVideoProctoringLoading(true);
      const data = await getVideoProctoringConfig();
      setVideoProctoringEnabled(data.video_proctoring_enabled);
    } catch (error) {
      console.error("Error fetching video proctoring:", error);
    } finally {
      setVideoProctoringLoading(false);
    }
  };
  
  const fetchReminderConfig = async () => {
    try {
      setReminderLoading(true);
      const data = await getReminderConfig();
      setReminderConfig(data);
      setReminderMessage({ type: "", text: "" });
    } catch (error) {
      console.error("Error fetching reminder config:", error);
      setReminderMessage({
        type: "error",
        text: "Error loading reminder config: " + error.message,
      });
    } finally {
      setReminderLoading(false);
    }
  };

  const fetchJobDivaSettings = async () => {
    try {
      setJobDivaLoading(true);
      const data = await getJobDivaSettings();
      setJobDivaSettings({
        clientId: data.clientId ?? "",
        username: data.username ?? "",
        password: "",
        passwordDisplay: data.password ?? "********",
      });
      setJobDivaMessage({ type: "", text: "" });
    } catch (error) {
      console.error("Error fetching JobDiva settings:", error);
      setJobDivaMessage({
        type: "error",
        text: "Error loading JobDiva settings: " + error.message,
      });
    } finally {
      setJobDivaLoading(false);
    }
  };

  // Fetch all settings on component mount
  useEffect(() => {
    if (!effectiveCanViewSettings) return;

    const fetchInterviewSettings = async () => {
      try {
        setInterviewLoading(true);
        const data = await getInterviewSettings();
        setInterviewSettings(data);
        setInterviewMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching interview settings:", error);
        setInterviewMessage({
          type: "error",
          text: "Error loading interview settings: " + error.message,
        });
      } finally {
        setInterviewLoading(false);
      }
    };

    const fetchLanguageSettings = async () => {
      try {
        setLanguageLoading(true);
        const currentLangData = await getCurrentLanguage();
        const capabilityData = await getAvailableInterviewLanguages();
        const capabilities = capabilityData.languages || [];
        const availableLanguages = capabilities
          .filter((language) => language.available)
          .map((language) => language.language);
        const configuredLanguage = currentLangData.language || "English";

        setLanguageSettings({
          currentLanguage: availableLanguages.includes(configuredLanguage)
            ? configuredLanguage
            : (availableLanguages[0] || "English"),
          supportedLanguages: availableLanguages,
          capabilities,
        });
        setLanguageMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching language settings:", error);
        setLanguageMessage({
          type: "error",
          text: "Error loading language settings: " + error.message,
        });
      } finally {
        setLanguageLoading(false);
      }
    };

    const fetchSystemServiceSettings = async () => {
      try {
        setSystemServiceLoading(true);
        const data = await getSystemServiceSettings();
        setSystemServiceSettings(data);
        setSystemServiceMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching system service settings:", error);
        setSystemServiceMessage({
          type: "error",
          text: "Error loading system service settings: " + error.message,
        });
      } finally {
        setSystemServiceLoading(false);
      }
    };

    const fetchListingSettings = async () => {
      try {
        setListingLoading(true);
        const data = await getListingSettings();
        setListingSettings(data);
        setListingMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching listing settings:", error);
        setListingMessage({
          type: "error",
          text: "Error loading listing settings: " + error.message,
        });
      } finally {
        setListingLoading(false);
      }
    };

    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const rolesData = await getRoles();

        // Handle both array and object formats
        const roles = Array.isArray(rolesData)
          ? rolesData
          : Array.isArray(rolesData.roles)
            ? rolesData.roles
            : [];

        setRolesList(roles);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setRbacMessage({
          type: "error",
          text: "Error loading roles: " + error.message,
        });
      } finally {
        setRolesLoading(false);
      }
    };

    const fetchRbacSettings = async () => {
      try {
        const rbacData = await getRBACSettings();
        setRbacSettings(rbacData || { DEFAULT_USER_ROLE_ID: null });
        setRbacMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching RBAC settings:", error);
        setRbacMessage({
          type: "error",
          text: "Error loading RBAC settings: " + error.message,
        });
      }
    };

    fetchInterviewSettings();
    fetchLanguageSettings();
    window.addEventListener("interview-provider-config-changed", fetchLanguageSettings);
    fetchSystemServiceSettings();
    fetchListingSettings();
    fetchRbacSettings();
    fetchRoles();
    fetchEmailConfig();
    fetchVideoProctoring();
    fetchReminderConfig();
    fetchJobDivaSettings();
    return () => window.removeEventListener("interview-provider-config-changed", fetchLanguageSettings);
  }, [effectiveCanViewSettings]);

  // Validation functions
  const validateRbacSettings = () => {
    const newErrors = {};

    if (!rbacSettings.DEFAULT_USER_ROLE_ID) {
      newErrors.DEFAULT_USER_ROLE_ID = "Please select a default user role";
    }

    setRbacErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateInterviewSettings = () => {
    const newErrors = {};

    if (
      !interviewSettings.MARKS_PER_QUESTION ||
      interviewSettings.MARKS_PER_QUESTION < 1
    ) {
      newErrors.MARKS_PER_QUESTION = "Marks per question must be at least 1";
    }

    if (
      !interviewSettings.PASSING_SCORE_PERCENTAGE ||
      interviewSettings.PASSING_SCORE_PERCENTAGE < 0 ||
      interviewSettings.PASSING_SCORE_PERCENTAGE > 100
    ) {
      newErrors.PASSING_SCORE_PERCENTAGE =
        "Passing score must be between 0 and 100";
    }

    if (
      !interviewSettings.INTERVIEW_LINK_EXPIRY ||
      interviewSettings.INTERVIEW_LINK_EXPIRY < 1
    ) {
      newErrors.INTERVIEW_LINK_EXPIRY =
        "Interview link expiry must be at least 1 minute";
    }

    setInterviewErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateListingSettings = () => {
    const newErrors = {};

    if (
      !listingSettings.JOB_OPENING_DEFAULT_DAYS ||
      listingSettings.JOB_OPENING_DEFAULT_DAYS < 1
    ) {
      newErrors.JOB_OPENING_DEFAULT_DAYS =
        "Job opening default days must be at least 1";
    }

    if (
      !listingSettings.SCHEDULE_DEFAULT_DAYS ||
      listingSettings.SCHEDULE_DEFAULT_DAYS < 1
    ) {
      newErrors.SCHEDULE_DEFAULT_DAYS =
        "Schedule default days must be at least 1";
    }

    setListingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateJobDivaSettings = () => {
    const newErrors = {};

    if (!jobDivaSettings.clientId || Number(jobDivaSettings.clientId) <= 0) {
      newErrors.clientId = "Client ID is required";
    }

    if (!jobDivaSettings.username || !jobDivaSettings.username.trim()) {
      newErrors.username = "Username is required";
    }

    setJobDivaErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save functions
  const saveInterviewSettings = async () => {
    if (!validateInterviewSettings()) return;

    if (!effectiveCanEditSettings) {
      setInterviewMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm Interview Settings",
      message:
        "Are you sure you want to save these interview configuration changes? This will affect all future interviews.",
      onConfirm: async () => {
        try {
          setInterviewLoading(true);
          const data = await updateInterviewSettings(interviewSettings);
          setInterviewSettings(data);
          setInterviewMessage({
            type: "success",
            text: "Interview settings saved successfully!",
          });
          setTimeout(() => setInterviewMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          console.error("Error saving interview settings:", error);
          setInterviewMessage({
            type: "error",
            text: "Error saving interview settings: " + error.message,
          });
        } finally {
          setInterviewLoading(false);
        }
      },
    });
  };

  const saveLanguageSettings = async () => {
    if (!effectiveCanEditSettings) {
      setLanguageMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setLanguageLoading(true);
      await setGlobalLanguage(languageSettings.currentLanguage);
      setLanguageMessage({
        type: "success",
        text: "Language settings saved successfully!",
      });
      setTimeout(() => setLanguageMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error saving language settings:", error);
      setLanguageMessage({
        type: "error",
        text: "Error saving language settings: " + error.message,
      });
    } finally {
      setLanguageLoading(false);
    }
  };

  const saveSystemServiceSettings = async () => {
    if (!effectiveCanEditSettings) {
      setSystemServiceMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setSystemServiceLoading(true);
      const data = await updateSystemServiceSettings(systemServiceSettings);
      setSystemServiceSettings(data);
      setSystemServiceMessage({
        type: "success",
        text: "System service settings saved successfully!",
      });
      setTimeout(() => setSystemServiceMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error saving system service settings:", error);
      setSystemServiceMessage({
        type: "error",
        text: "Error saving system service settings: " + error.message,
      });
    } finally {
      setSystemServiceLoading(false);
    }
  };

  const saveListingSettings = async () => {
    if (!validateListingSettings()) return;

    if (!effectiveCanEditSettings) {
      setListingMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setListingLoading(true);
      const data = await updateListingSettings(listingSettings);
      setListingSettings(data);
      setListingMessage({
        type: "success",
        text: "Listing settings saved successfully!",
      });
      setTimeout(() => setListingMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error saving listing settings:", error);
      setListingMessage({
        type: "error",
        text: "Error saving listing settings: " + error.message,
      });
    } finally {
      setListingLoading(false);
    }
  };

  const saveRbacSettings = async () => {
    if (!validateRbacSettings()) return;

    if (!effectiveCanEditSettings) {
      setRbacMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm RBAC Settings",
      message:
        "Are you sure you want to save these RBAC configuration changes? This will affect default user role assignments.",
      onConfirm: async () => {
        try {
          setRbacLoading(true);
          const data = await updateRBACSettings(rbacSettings);
          setRbacSettings(data);
          setRbacMessage({
            type: "success",
            text: "RBAC settings saved successfully!",
          });
          setTimeout(() => setRbacMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          console.error("Error saving RBAC settings:", error);
          setRbacMessage({
            type: "error",
            text: "Error saving RBAC settings: " + error.message,
          });
        } finally {
          setRbacLoading(false);
        }
      },
    });
  };

  const saveEmailConfig = async () => {
    if (!effectiveCanEditSettings) {
      setEmailMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm Email Configuration",
      message:
        "Are you sure you want to update email settings? This will affect all interview notifications.",
      onConfirm: async () => {
        try {
          setEmailLoading(true);

          await updateEmailConfig({
            schedule_email_enabled: emailConfig.schedule_email_enabled,
          });

          setEmailMessage({
            type: "success",
            text: "Email config updated successfully!",
          });

          setTimeout(() => setEmailMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          setEmailMessage({
            type: "error",
            text: "Error updating email config: " + error.message,
          });
        } finally {
          setEmailLoading(false);
        }
      },
    });
  };

  const saveVideoProctoring = () => {
    if (!effectiveCanEditSettings) {
      setSystemServiceMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm Video Proctoring Settings",
      message:
        "Are you sure you want to update video proctoring settings? This will affect interview photo requirements.",
      onConfirm: async () => {
        try {
          setVideoProctoringLoading(true);

          await updateVideoProctoringConfig({
            video_proctoring_enabled: videoProctoringEnabled,
          });

          setSystemServiceMessage({
            type: "success",
            text: "Video proctoring updated successfully!",
          });

          setTimeout(() => {
            setSystemServiceMessage({ type: "", text: "" });
          }, 5000);
        } catch (error) {
          setSystemServiceMessage({
            type: "error",
            text: "Error updating video proctoring",
          });
        } finally {
          setVideoProctoringLoading(false);
        }
      },
    });
  };
  
  const saveReminderConfig = async () => {
    if (!effectiveCanEditSettings) {
      setReminderMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm Reminder Configuration",
      message:
        "Are you sure you want to update reminder settings? This will affect interview reminder notifications.",
      onConfirm: async () => {
        try {
          setReminderLoading(true);
          const data = await updateReminderConfig(reminderConfig);
          setReminderConfig(data.config);
          setReminderMessage({
            type: "success",
            text: "Reminder config updated successfully!",
          });
          setTimeout(() => setReminderMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          console.error("Error saving reminder config:", error);
          setReminderMessage({
            type: "error",
            text: "Error saving reminder config: " + error.message,
          });
        } finally {
          setReminderLoading(false);
        }
      },
    });
  };

  const saveJobDivaSettings = async () => {
    if (!validateJobDivaSettings()) return;

    if (!effectiveCanEditSettings) {
      setJobDivaMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    try {
      setJobDivaLoading(true);
      const payload = {
        clientId: Number(jobDivaSettings.clientId),
        username: jobDivaSettings.username.trim(),
        ...(jobDivaSettings.password && jobDivaSettings.password.trim()
          ? { password: jobDivaSettings.password.trim() }
          : {}),
      };
      const data = await updateJobDivaSettings(payload);
      setJobDivaSettings({
        clientId: data.clientId ?? "",
        username: data.username ?? "",
        password: "",
        passwordDisplay: data.password ?? "********",
      });
      setJobDivaMessage({
        type: "success",
        text: "JobDiva settings saved successfully!",
      });
      setTimeout(() => setJobDivaMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error saving JobDiva settings:", error);
      setJobDivaMessage({
        type: "error",
        text: "Error saving JobDiva settings: " + error.message,
      });
    } finally {
      setJobDivaLoading(false);
    }
  };

  return (
    <div className="settings-interview-grid">
      <SettingsSection
        icon={Clock}
        title="Interview Configuration"
        description="Configure marks per question, passing score, and link expiry settings"
      >
        <div className="space-y-8">
          {interviewMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                interviewMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {interviewMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${interviewMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {interviewMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <InputField
              label="Marks Per Question"
              type="number"
              value={interviewSettings.MARKS_PER_QUESTION}
              onChange={(e) => {
                setInterviewSettings((prevSettings) => ({
                  ...prevSettings,
                  MARKS_PER_QUESTION: parseInt(e.target.value),
                }));
                if (interviewErrors.MARKS_PER_QUESTION) {
                  setInterviewErrors((prevErrors) => ({
                    ...prevErrors,
                    MARKS_PER_QUESTION: "",
                  }));
                }
              }}
              error={interviewErrors.MARKS_PER_QUESTION}
              placeholder="10"
              helpText="Points awarded for each interview question"
              canEdit={effectiveCanEditSettings}
            />

            <InputField
              label="Passing Score Percentage"
              type="number"
              value={interviewSettings.PASSING_SCORE_PERCENTAGE}
              onChange={(e) => {
                setInterviewSettings((prevSettings) => ({
                  ...prevSettings,
                  PASSING_SCORE_PERCENTAGE: parseInt(e.target.value),
                }));
                if (interviewErrors.PASSING_SCORE_PERCENTAGE) {
                  setInterviewErrors((prevErrors) => ({
                    ...prevErrors,
                    PASSING_SCORE_PERCENTAGE: "",
                  }));
                }
              }}
              error={interviewErrors.PASSING_SCORE_PERCENTAGE}
              placeholder="75"
              helpText="Minimum percentage score to pass"
              canEdit={effectiveCanEditSettings}
            />

            <InputField
              label="Interview Link Expiry (minutes)"
              type="number"
              value={interviewSettings.INTERVIEW_LINK_EXPIRY}
              onChange={(e) => {
                setInterviewSettings((prevSettings) => ({
                  ...prevSettings,
                  INTERVIEW_LINK_EXPIRY: parseInt(e.target.value),
                }));
                if (interviewErrors.INTERVIEW_LINK_EXPIRY) {
                  setInterviewErrors((prevErrors) => ({
                    ...prevErrors,
                    INTERVIEW_LINK_EXPIRY: "",
                  }));
                }
              }}
              error={interviewErrors.INTERVIEW_LINK_EXPIRY}
              placeholder="40"
              helpText="Minutes before interview link expires"
              canEdit={effectiveCanEditSettings}
            />
          </div>

          <div className="settings-interview-save">
            <button
              onClick={saveInterviewSettings}
              disabled={!effectiveCanEditSettings || interviewLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !interviewLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {interviewLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {interviewLoading ? "Saving..." : "Save Interview Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Lock}
        title="JobDiva Integration"
        description="Manage the shared JobDiva credentials used for importing job openings"
      >
        <div className="space-y-8">
          {jobDivaMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                jobDivaMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {jobDivaMessage.type === "success" ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              )}
              <p
                className={`text-sm font-medium ${jobDivaMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {jobDivaMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <InputField
              label="Client ID"
              type="number"
              value={jobDivaSettings.clientId}
              onChange={(e) => {
                setJobDivaSettings((prev) => ({ ...prev, clientId: e.target.value }));
                if (jobDivaErrors.clientId) {
                  setJobDivaErrors((prev) => ({ ...prev, clientId: "" }));
                }
              }}
              error={jobDivaErrors.clientId}
              placeholder="1247"
              helpText="Shared JobDiva client ID"
              canEdit={effectiveCanEditSettings}
            />

            <InputField
              label="Username"
              type="text"
              value={jobDivaSettings.username}
              onChange={(e) => {
                setJobDivaSettings((prev) => ({ ...prev, username: e.target.value }));
                if (jobDivaErrors.username) {
                  setJobDivaErrors((prev) => ({ ...prev, username: "" }));
                }
              }}
              error={jobDivaErrors.username}
              placeholder="user@example.com"
              helpText="Shared JobDiva account username"
              canEdit={effectiveCanEditSettings}
            />

            <InputField
              label="Password"
              type="password"
              value={jobDivaSettings.passwordDisplay}
              onChange={(e) => {
                const nextValue = e.target.value;
                setJobDivaSettings((prev) => ({
                  ...prev,
                  password: nextValue,
                  passwordDisplay: nextValue,
                }));
                if (jobDivaErrors.password) {
                  setJobDivaErrors((prev) => ({ ...prev, password: "" }));
                }
              }}
              error={jobDivaErrors.password}
              placeholder="Enter a new password"
              helpText="Leave blank to keep the existing password"
              canEdit={effectiveCanEditSettings}
            />
          </div>

          <div className="settings-interview-save">
            <button
              onClick={saveJobDivaSettings}
              disabled={!effectiveCanEditSettings || jobDivaLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !jobDivaLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {jobDivaLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {jobDivaLoading ? "Saving..." : "Save JobDiva Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Mail}
        title="Interview Email Configuration"
        description="Enable or disable interview schedule email notifications"
      >
        <div className="space-y-8">
          {emailMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                emailMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              <p className="text-sm font-medium">{emailMessage.text}</p>
            </div>
          )}

          <ToggleSwitch
            label="Auto Email sent"
            checked={emailConfig.schedule_email_enabled}
            onChange={(checked) =>
              setEmailConfig({ schedule_email_enabled: checked })
            }
            description="Send email invite automatically when interviews are scheduled"
            canEdit={effectiveCanEditSettings}
          />

          <div className="settings-interview-save">
            <button
              onClick={saveEmailConfig}
              disabled={!effectiveCanEditSettings || emailLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !emailLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
            >
              {emailLoading ? "Saving..." : "Save Email Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Video}
        title="Interview Proctoring Configuration"
        description="Enable or disable candidate video/photo verification during interviews"
      >
        <div className="space-y-6">
          {/* ✅ ADD MESSAGE HERE */}
          {systemServiceMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                systemServiceMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              <p className="text-sm font-medium">{systemServiceMessage.text}</p>
            </div>
          )}

          {/* 🔽 Toggle */}
          <ToggleSwitch
            label="Enable Video Proctoring"
            checked={videoProctoringEnabled}
            onChange={setVideoProctoringEnabled}
            description="If enabled, candidate photo upload becomes mandatory"
            canEdit={effectiveCanEditSettings}
          />

          {/* 🔽 Button (fixed version) */}
          <div className="settings-interview-save">
            <button
              onClick={saveVideoProctoring}
              disabled={!effectiveCanEditSettings || videoProctoringLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !videoProctoringLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
            >
              {videoProctoringLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {videoProctoringLoading
                ? "Saving..."
                : "Save Proctoring Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Sparkles}
        title="Interview Language Config"
        description="Configure the default language for interviews and system communications"
      >
        <div className="space-y-8">
          {languageMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                languageMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {languageMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${languageMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {languageMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <SelectField
              label="Select Language"
              value={languageSettings.currentLanguage}
              onChange={(e) =>
                setLanguageSettings((prev) => ({
                  ...prev,
                  currentLanguage: e.target.value,
                }))
              }
              options={languageSettings.supportedLanguages.map((lang) => ({
                value: lang,
                label: lang,
                icon: <span aria-hidden="true">{languageFlag(lang)}</span>,
              }))}
              helpText="Choose the default language for all system communications"
              canEdit={effectiveCanEditSettings}
            />
          </div>

{/*           {languageSettings.capabilities.some((language) => !language.available) && (
            <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">Unavailable with the current provider configuration</p>
              {languageSettings.capabilities
                .filter((language) => !language.available)
                .map((language) => (
                  <p key={language.language_code} className="text-xs text-amber-800">
                    {language.language}: {language.reason || "Required capability is unavailable."}
                  </p>
                ))}
            </div>
          )} */}

          <div className="settings-interview-save">
            <button
              onClick={saveLanguageSettings}
              disabled={!effectiveCanEditSettings || languageLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !languageLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {languageLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {languageLoading ? "Saving..." : "Save Language Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection icon={Lock} title="RBAC Configuration" description="Configure default role assignments and access control settings">
        {/* Content */}
        <div className="space-y-8">
          {rbacMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                rbacMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {rbacMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${rbacMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {rbacMessage.text}
              </p>
            </div>
          )}

          <div className="settings-interview-role">
            <SelectField label="Default User Role" value={rbacSettings.DEFAULT_USER_ROLE_ID || ""} onChange={(e) => {
                setRbacSettings((prev) => ({
                  ...prev,
                  DEFAULT_USER_ROLE_ID: parseInt(e.target.value),
                }));
                if (rbacErrors.DEFAULT_USER_ROLE_ID) {
                  setRbacErrors((prev) => ({
                    ...prev,
                    DEFAULT_USER_ROLE_ID: "",
                  }));
                }
              }} options={[{ value: "", label: rolesLoading ? "Loading roles..." : "Select a role..." }, ...rolesList.map(role => ({ value: role.id, label: role.name }))]} canEdit={effectiveCanEditSettings && !rolesLoading} />

            {rbacErrors.DEFAULT_USER_ROLE_ID && (
              <p className="text-xs text-red-600 font-medium flex items-center gap-1 mt-2">
                <AlertTriangle className="w-3 h-3" />
                {rbacErrors.DEFAULT_USER_ROLE_ID}
              </p>
            )}

            <p className="text-sm text-gray-600 mt-3">
              Default role assigned to new users during registration
            </p>
          </div>

          {/* Info Box */}
          <div className="settings-info-banner">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Info:</span> The default role is
              automatically assigned to new users when they register. Select
              from the available roles in your system.
            </p>
          </div>

          {/* Save Button */}
          <div className="settings-interview-save">
            <button
              onClick={saveRbacSettings}
              disabled={
                !effectiveCanEditSettings || rbacLoading || rolesLoading
              }
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !rbacLoading && !rolesLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {rbacLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {rbacLoading ? "Saving..." : "Save RBAC Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Video}
        title="System Service Configuration"
        description="Configure system check and second camera settings"
      >
        <div className="space-y-8">
          {systemServiceMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                systemServiceMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {systemServiceMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${systemServiceMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {systemServiceMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <ToggleSwitch
              label="Enable System Check"
              checked={systemServiceSettings.SYSTEM_CHECK_ENABLE}
              onChange={(checked) =>
                setSystemServiceSettings((prev) => ({
                  ...prev,
                  SYSTEM_CHECK_ENABLE: checked,
                }))
              }
              description="Require system compatibility check before interview starts"
              canEdit={effectiveCanEditSettings}
            />

            <ToggleSwitch
              label="Enable Second Camera"
              checked={systemServiceSettings.SECOND_CAMERA_ENABLE}
              onChange={(checked) =>
                setSystemServiceSettings((prev) => ({
                  ...prev,
                  SECOND_CAMERA_ENABLE: checked,
                }))
              }
              description="Require candidates to enable a second camera view during interview"
              canEdit={effectiveCanEditSettings}
            />

            <ToggleSwitch
              label="Enable Code Evaluation"
              checked={systemServiceSettings.CODE_EVALUATION_ENABLE}
              onChange={(checked) =>
                setSystemServiceSettings((prev) => ({
                  ...prev,
                  CODE_EVALUATION_ENABLE: checked,
                }))
              }
              description="Enable code evaluation and coding challenges during interview"
              canEdit={effectiveCanEditSettings}
            />

            <ToggleSwitch
              label="Enable Resume Evaluation"
              checked={systemServiceSettings.RESUME_EVALUATION_ENABLE}
              onChange={(checked) =>
                setSystemServiceSettings((prev) => ({
                  ...prev,
                  RESUME_EVALUATION_ENABLE: checked,
                }))
              }
              description="Enable automated resume parsing and evaluation"
              canEdit={effectiveCanEditSettings}
            />
          </div>

          <div className="settings-info-banner">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Info:</span> These settings apply
              to all new interviews. Enable system check to ensure candidates'
              systems meet minimum requirements.
            </p>
          </div>

          <div className="settings-interview-save">
            <button
              onClick={saveSystemServiceSettings}
              disabled={!effectiveCanEditSettings || systemServiceLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !systemServiceLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {systemServiceLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {systemServiceLoading
                ? "Saving..."
                : "Save System Service Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Clock}
        title="Listing Configuration"
        description="Configure default days for job openings and scheduled interviews"
      >
        <div className="space-y-8">
          {listingMessage.text && (
            <div
              className={`p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                listingMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {listingMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${listingMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {listingMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <InputField
              label="Job Opening Default Days"
              type="number"
              value={listingSettings.JOB_OPENING_DEFAULT_DAYS}
              onChange={(e) => {
                setListingSettings((prev) => ({
                  ...prev,
                  JOB_OPENING_DEFAULT_DAYS: parseInt(e.target.value),
                }));
                if (listingErrors.JOB_OPENING_DEFAULT_DAYS) {
                  setListingErrors((prev) => ({
                    ...prev,
                    JOB_OPENING_DEFAULT_DAYS: "",
                  }));
                }
              }}
              error={listingErrors.JOB_OPENING_DEFAULT_DAYS}
              placeholder="30"
              helpText="Default number of days to display job openings"
              canEdit={effectiveCanEditSettings}
            />

            <InputField
              label="View Interview Default Days"
              type="number"
              value={listingSettings.SCHEDULE_DEFAULT_DAYS}
              onChange={(e) => {
                setListingSettings((prev) => ({
                  ...prev,
                  SCHEDULE_DEFAULT_DAYS: parseInt(e.target.value),
                }));
                if (listingErrors.SCHEDULE_DEFAULT_DAYS) {
                  setListingErrors((prev) => ({
                    ...prev,
                    SCHEDULE_DEFAULT_DAYS: "",
                  }));
                }
              }}
              error={listingErrors.SCHEDULE_DEFAULT_DAYS}
              placeholder="15"
              helpText="Default number of days to display scheduled interviews"
              canEdit={effectiveCanEditSettings}
            />
          </div>

          <div className="settings-info-banner">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Info:</span> These settings
              control the default time period for displaying job openings and
              scheduled interviews in the listings. Dates beyond these periods
              will be filtered by default.
            </p>
          </div>

          <div className="settings-interview-save">
            <button
              onClick={saveListingSettings}
              disabled={!effectiveCanEditSettings || listingLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !listingLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {listingLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {listingLoading ? "Saving..." : "Save Listing Settings"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        icon={Bell}
        title="Reminder Configurations"
        description="Configure interview reminder notifications"
      >
        <div className="space-y-8">
          {reminderMessage.text && (
            <div
              className={`settings-reminder-message p-4 sm:p-5 border rounded-xl flex items-center gap-3 ${
                reminderMessage.type === "success"
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              {reminderMessage.type === "success" ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p
                className={`text-sm font-medium ${reminderMessage.type === "success" ? "text-green-700" : "text-red-700"}`}
              >
                {reminderMessage.text}
              </p>
            </div>
          )}
          
          <ToggleSwitch
            label="Enable Reminders"
            checked={reminderConfig.enabled}
            onChange={(checked) =>
              setReminderConfig((prev) => ({ ...prev, enabled: checked }))
            }
            description="Enable or disable all interview reminder notifications"
            canEdit={effectiveCanEditSettings}
          />
          
          {reminderConfig.enabled && (
            <div className="space-y-8">
              <div className="settings-reminder-count">
                <SelectField label="Number of Reminders" value={reminderConfig.reminders.length} onChange={(e) => {
                    const numReminders = parseInt(e.target.value);
                    const defaultReminders = [
                      { offset_minutes: 1440, offset_label: "1 day before" },
                      { offset_minutes: 360, offset_label: "6 hours before" },
                      { offset_minutes: 60, offset_label: "1 hour before" }
                    ];
                    setReminderConfig((prev) => ({
                      ...prev,
                      reminders: defaultReminders.slice(0, numReminders)
                    }));
                  }} options={[1, 2, 3].map(count => ({ value: count, label: `${count} ${count === 1 ? "Reminder" : "Reminders"}` }))} canEdit={effectiveCanEditSettings && !reminderLoading} />
              </div>
              
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-800 tracking-tight">
                  Configure Reminders
                </label>
                {reminderConfig.reminders.map((reminder, index) => (
                  <div key={index} className="settings-reminder-row">
                    <span className="settings-reminder-number" aria-hidden="true">
                      {index + 1}
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Offset Minutes
                        </label>
                        <input
                          type="number"
                          value={reminder.offset_minutes}
                          onChange={(e) => {
                            const newReminders = [...reminderConfig.reminders];
                            newReminders[index] = {
                              ...reminder,
                              offset_minutes: parseInt(e.target.value) || 0
                            };
                            setReminderConfig((prev) => ({
                              ...prev,
                              reminders: newReminders
                            }));
                          }}
                          disabled={!effectiveCanEditSettings || reminderLoading}
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all ${
                            effectiveCanEditSettings && !reminderLoading
                              ? "border-gray-300 bg-white text-gray-900 focus:border-teal-500 focus:ring-1 focus:ring-teal-200"
                              : "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed opacity-60"
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={reminder.offset_label}
                          onChange={(e) => {
                            const newReminders = [...reminderConfig.reminders];
                            newReminders[index] = {
                              ...reminder,
                              offset_label: e.target.value
                            };
                            setReminderConfig((prev) => ({
                              ...prev,
                              reminders: newReminders
                            }));
                          }}
                          disabled={!effectiveCanEditSettings || reminderLoading}
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all ${
                            effectiveCanEditSettings && !reminderLoading
                              ? "border-gray-300 bg-white text-gray-900 focus:border-teal-500 focus:ring-1 focus:ring-teal-200"
                              : "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed opacity-60"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="settings-interview-save">
            <button
              onClick={saveReminderConfig}
              disabled={!effectiveCanEditSettings || reminderLoading}
              className={`flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 ${
                effectiveCanEditSettings && !reminderLoading
                  ? "bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg"
                  : "bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}
              title={
                !effectiveCanEditSettings
                  ? "You do not have permission to edit settings"
                  : ""
              }
            >
              {reminderLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {reminderLoading ? "Saving..." : "Save Reminder Configurations"}
            </button>
          </div>
        </div>
      </SettingsSection>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog({ ...confirmationDialog, open: false })
        }
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Save Changes"
        cancelText="Cancel"
        isDangerous={false}
      />
    </div>
  );
};

export default InterviewSettings;
